(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Bundle Shipping Sync
  // ---------------------------------------------------------------------
  // Pairs with `snippets/bundle-inbound-option.liquid` and the
  // `data-shipping-role` / `data-readonly` attributes added by
  // `snippets/shipping-option-picker.liquid`, plus `data-main-product-id`
  // on `.bundle_form` (snippets/bundle-form.liquid).
  //
  // The sync group for a given picker is scoped to ONE bundle: every picker
  // inside the SAME `.bundle_form` element, plus every picker for that
  // bundle's main product outside any bundle form (found by matching
  // `data-product-id` against the bundle form's `data-main-product-id`). This
  // is deliberately NOT a single global group — a page can carry more than one
  // `.bundle_form` (e.g. two independent bundle sections) or pickers for OTHER
  // products outside any bundle_form, and none of those should react to this
  // bundle's selection.
  //
  // Membership is NOT filtered by `data-shipping-role` here, and the reason is
  // specific. Liquid stamps that attribute from the MAIN product's variant at
  // PAGE LOAD (bundle-form.liquid -> bundle-inbound-option.liquid), and the
  // bundle is never re-rendered afterwards: sections/main-product.liquid sets
  // allow-partial-rerender, and theme.js's partial path replaces only a fixed
  // list of block types — for buy-buttons only the <buy-buttons> element
  // itself, not the bundle rendered beside it. On a product whose first
  // variant is in stock the main product has no picker at all, so
  // main_inbound_mode is blank, every card is stamped "bundle-independent",
  // and the roles never recover once the shopper moves to a backordered
  // variant. Filtering on them made the whole feature depend on which variant
  // the page happened to load with.
  //
  // So the group is every card, and `propagate` asks each one whether its
  // CURRENT variant can honour the option (btoSupportsOption). That is what
  // the feature was always specified as: a bundle product follows the main
  // product's selection when that option is valid for it too.
  //
  // A page also carries more than one picker for the SAME product: the main
  // buy box and snippets/../pdp-buy-box-repeat each render their own, with
  // their own product form id. Those are peers and stay in step, so the
  // product cannot show two different pre-order prices at once — the price,
  // badge and CTA now follow the selected shipping option
  // (assets/shipping-option.js, togglePreorderDiscountUI), which makes a
  // divergence between the two boxes visible as a price difference rather
  // than just a radio difference.
  //
  // Only the main product's own picker(s) may DRIVE — never a `data-readonly`
  // follower, and never another product's card inside the bundle. Widening
  // membership above did not widen authority: isDriverCandidate still admits
  // only the bundle's own main card (role "bundle-sync", which
  // bundle-inbound-option.liquid always gives it) and pickers outside any
  // bundle form. Picking an option on a driver propagates it to every member
  // whose current variant can honour it.
  //
  // This relies on assets/shipping-option.js already having wired up each
  // container's radios (class toggling, surcharge label, dual-span summary,
  // `.bundle_form`'s own `change` listener for price/button state). Setting
  // `.checked` + dispatching `change` re-enters THIS script's own listeners
  // too (every driver in the group is wired the same way), so `propagate`
  // is guarded by `isPropagating`: only the outermost call is allowed to do
  // any work, every re-entrant call triggered by the events it dispatches is
  // a no-op. Without that guard a single click can recurse across every
  // container's listener before the call stack unwinds, which is what
  // produced runaway "Maximum call stack size exceeded" errors in
  // production the first time this shipped without it.
  // ---------------------------------------------------------------------

  var isPropagating = false;

  // bundleOnly restricts a non-bundle reference to its bundle cards, leaving
  // its non-bundle peers alone. Used by the startup alignment pass below; see
  // the note there for why peer-to-peer alignment must not happen at that
  // point.
  function getSyncGroup(referenceContainer, bundleOnly) {
    var group = [];
    var refBundleForm = referenceContainer.closest('.bundle_form');

    if (refBundleForm) {
      var mainProductId = refBundleForm.getAttribute('data-main-product-id');
      document.querySelectorAll('[data-shipping-option-picker]').forEach(function (container) {
        var containerBundleForm = container.closest('.bundle_form');
        if (containerBundleForm === refBundleForm) {
          group.push(container);
        } else if (!containerBundleForm && container.getAttribute('data-product-id') === mainProductId) {
          group.push(container);
        }
      });
    } else {
      // Reference is outside any bundle_form (a PDP buy box picker, or an
      // unrelated picker elsewhere on the page). Join bundle grid(s) whose
      // main product is THIS exact product, plus the product's other
      // non-bundle pickers — its peers in the sense described above.
      var productId = referenceContainer.getAttribute('data-product-id');
      if (productId) {
        document
          .querySelectorAll(
            '.bundle_form[data-main-product-id="' +
              productId +
              '"] [data-shipping-option-picker]'
          )
          .forEach(function (container) {
            group.push(container);
          });

        if (!bundleOnly) {
          document
            .querySelectorAll('[data-shipping-option-picker][data-product-id="' + productId + '"]')
            .forEach(function (container) {
              if (container === referenceContainer) return;
              if (container.closest('.bundle_form')) return;
              group.push(container);
            });
        }
      }
    }

    return group;
  }

  function getCheckedValue(container) {
    var checked = container.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  // A driver is allowed to propagate its own changes: never a
  // `data-readonly` follower, and — for a container inside a `.bundle_form`
  // — only the main product's own card (role="bundle-sync"). A
  // "bundle-independent" or "bundle-fixed" card inside the SAME bundle_form
  // must never wire a propagation listener, or picking its own (unrelated)
  // option would incorrectly force that value onto the main product and
  // every card that mirrors it.
  function isDriverCandidate(container) {
    if (container.hasAttribute('data-readonly')) return false;
    var inBundleForm = !!container.closest('.bundle_form');
    return !inBundleForm || container.getAttribute('data-shipping-role') === 'bundle-sync';
  }

  function propagate(sourceContainer, value, bundleOnly) {
    if (isPropagating) return;
    isPropagating = true;
    try {
      getSyncGroup(sourceContainer, bundleOnly).forEach(function (container) {
        if (container === sourceContainer) return;
        var radio = container.querySelector('input[type="radio"][value="' + value + '"]');
        if (!radio || radio.checked) return;
        // Does this card's CURRENT variant offer that option at all? Forcing
        // one it cannot honour would submit a _shipping_option the variant has
        // no lane for (snippets/inbound-mode.liquid: an Air-only variant has no
        // Ocean, an Ocean-only variant no Air). Liquid's role assignment
        // ("bundle-fixed" for a card whose option set is a strict subset of
        // main's) already keeps most such cards out of the sync group; this
        // covers a card whose variant changed after render.
        //
        // shipping-option.js owns that answer, from the per-variant payload,
        // and exposes it as btoSupportsOption. This used to read the swatch's
        // `hidden` instead, which looks equivalent and is not: a read-only
        // follower hides every swatch but the selected one, so the swatch we
        // need to switch TO is always hidden there and every follower was
        // skipped. That is the 2026-09-20 (9d5ace03) regression — bundle sync
        // appeared to stop working while each card still moved on its own.
        //
        // The swatch check survives only for a container shipping-option.js has
        // not initialized yet, where `hidden` is still the server's render and
        // does mean "not offered".
        if (typeof container.btoSupportsOption === 'function') {
          if (!container.btoSupportsOption(value)) return;
        } else {
          var swatch = radio.closest('[data-shipping-swatch]');
          if (swatch && swatch.hidden) return;
        }
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      isPropagating = false;
    }
  }

  function wire(container) {
    if (container.dataset.bundleSyncWired === 'true') return;
    container.dataset.bundleSyncWired = 'true';

    // Only a driver may ever originate a propagation — see
    // isDriverCandidate for exactly which containers that is (and why).
    if (!isDriverCandidate(container)) return;

    container.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        propagate(container, radio.value);
      });
    });
  }

  function init() {
    var containers = document.querySelectorAll('[data-shipping-option-picker]');
    containers.forEach(wire);

    // Align each driver's own scoped group to whatever it currently has
    // selected (a no-op on first paint — server-rendered default is Ocean
    // everywhere — but matters after a variant:change re-render leaves a
    // driver out of step with its group).
    //
    // Deliberately bundle-only. Between two peer buy boxes there is no way to
    // tell here which one holds the customer's actual choice, so whichever the
    // document happened to list first would win and could overwrite it with a
    // freshly re-rendered box's server default. Peers converge without this:
    // shipping-option.js keys its remembered selection by product id, which
    // both boxes share, and re-applies it whenever a container initializes.
    // Peer propagation therefore only happens on a real user change, below.
    containers.forEach(function (container) {
      if (!isDriverCandidate(container)) return;
      var value = getCheckedValue(container);
      if (value) {
        propagate(container, value, true);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('variant:change', init);
})();
